import Tool, { type ToolArgs } from '../../../tools/Tool';
import { Transforms, type Editor, type Element } from 'slate';
import { ReactEditor } from 'slate-react';
import { isLinkCardElement } from '../../../types/guards';
import type { Placement } from '@floating-ui/dom';
import linkCardOrientationVertical from '../../../assets/icons/linkCardOrientationVertical.svg';
import linkCardOrientationHorizontal from '../../../assets/icons/linkCardOrientationHorizontal.svg';
import { svgToIcon } from '../../../components/Toolbar/toolbarUtils';
import type { LinkCardOrientation } from '../../../types/aha-editor';
import LinkCardCommon from '../LinkCardCommon';
import { hasImageOrPlaceholder } from '../utils';

const orientationIcons = {
  vertical: linkCardOrientationVertical,
  horizontal: linkCardOrientationHorizontal,
};

function updateOrientation(
  editor: Editor | undefined,
  element: Element | undefined,
  value: LinkCardOrientation
) {
  if (!editor || !element || !value || !isLinkCardElement(element)) return;
  const path = ReactEditor.findPath(editor, element);
  if (!path) return;
  Transforms.setNodes(
    editor,
    {
      linkCard_orientation: value,
    },
    {
      at: path,
    }
  );

  // Store the orientation preference for future LinkCard insertions
  localStorage.setItem(LinkCardCommon.linkCardOrientationPrefKey, value);
}

class ToolLinkCardOrientationTool extends Tool {
  static toolName = 'Edit orientation';
  static orientationValue: LinkCardOrientation = 'vertical';
  static linkCardIcon = linkCardOrientationVertical;

  static tooltip(): string {
    return this.toolName;
  }

  static renderToolbarButtonIcon({ editor }: ToolArgs): JSX.Element {
    return svgToIcon(this.linkCardIcon);
  }

  static action({ editor, element, controller }: ToolArgs): void {
    updateOrientation(editor, element, this.orientationValue);
  }

  static isActive({ element }: ToolArgs): boolean {
    if (!element || !isLinkCardElement(element)) return false;
    return element.linkCard_orientation === this.orientationValue;
  }

  static isEnabled({ element }: ToolArgs): boolean {
    return Boolean(
      element &&
        isLinkCardElement(element) &&
        hasImageOrPlaceholder(element.linkCard_image)
    );
  }
}

class ToolLinkCardOrientationVertical extends ToolLinkCardOrientationTool {
  static toolName = 'Vertical orientation';
  static orientationValue: LinkCardOrientation = 'vertical';
  static linkCardIcon = linkCardOrientationVertical;
}

class ToolLinkCardOrientationHorizontal extends ToolLinkCardOrientationTool {
  static toolName = 'Horizontal orientation';
  static orientationValue: LinkCardOrientation = 'horizontal';
  static linkCardIcon = linkCardOrientationHorizontal;
}

class ToolLinkCardOrientation extends Tool {
  static toolName = 'Orientation';
  static withoutRefocus = true;

  static renderToolbarButtonIcon({ editor, element }: ToolArgs): JSX.Element {
    const orientation =
      element && isLinkCardElement(element)
        ? element.linkCard_orientation ?? 'vertical'
        : 'vertical';
    const orientationIcon = orientationIcons[orientation];
    return svgToIcon(orientationIcon);
  }

  static submenuTools = () => [
    [ToolLinkCardOrientationVertical, ToolLinkCardOrientationHorizontal],
  ];

  static tooltipPlacement(): Placement {
    return 'bottom';
  }

  static isEnabled({ element }: ToolArgs): boolean {
    if (!element || !isLinkCardElement(element)) return false;
    return hasImageOrPlaceholder(element.linkCard_image);
  }
}

export default ToolLinkCardOrientation;
